import React, { useCallback, useEffect, useState } from 'react';

/**
 * DisplayCredentialSection — admin-display-provisioning (slug 3 of the
 * presto-display-auth ADR). Provision / rotate / revoke a DISPLAY's
 * frame-fetch read key from Edit Station, without touching the device.
 *
 * The operator picks a target account; aveq-admin mints a read key (slug 1's
 * /admin/access/read-key) and writes it into this station's config (the
 * display-cred-via-config path the lite board polls) — so the screen picks it up
 * on its next config-refresh, NO re-flash. The plaintext key is NEVER shown here
 * (ADR D4, device-grade secret) — only its prefix + provisioned-at.
 *
 * BFF contract:
 *   GET    /api/user-access/accounts?q=<q>               -> {accounts:[{id,display_name,email}]}
 *   POST   /api/stations/{id}/display-credential {account_id}
 *                                                        -> {ok,account_id,key_prefix,provisioned_at}
 *   DELETE /api/stations/{id}/display-credential         -> {ok,revoked}
 *
 * Current status is read from the station config the host passes in
 * (display_account_id / display_key_prefix / display_provisioned_at).
 */
export default function DisplayCredentialSection({ stationId, config, onChanged }) {
  const [cred, setCred] = useState(() => {
    const c = config || {};
    return c.display_read_key_id
      ? { account_id: c.display_account_id, key_prefix: c.display_key_prefix,
          provisioned_at: c.display_provisioned_at }
      : null;
  });
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);
  // display-account-auto: 'existing' (pick an account) or 'create' (mint a
  // dedicated no-login display account granted just this station).
  const [mode, setMode] = useState('existing');
  const [newName, setNewName] = useState('');

  const search = useCallback(async (q) => {
    setError(null);
    if (!q || q.trim().length < 2) { setResults([]); return; }
    try {
      const r = await fetch(`/api/user-access/accounts?q=${encodeURIComponent(q.trim())}`,
                            { credentials: 'same-origin' });
      if (!r.ok) { setError(`account search: HTTP ${r.status}`); return; }
      const j = await r.json();
      setResults((j.accounts || []).slice(0, 20));
    } catch (e) { setError(String(e.message || e)); }
  }, []);

  // Debounce the typeahead so we don't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => search(query), 250);
    return () => clearTimeout(t);
  }, [query, search]);

  const provision = async () => {
    // display-account-auto: 'create' sends new_display_name (server mints a
    // dedicated no-login account granted this station); 'existing' sends account_id.
    const payload = mode === 'create'
      ? (newName.trim() ? { new_display_name: newName.trim() } : null)
      : (selected ? { account_id: selected.id } : null);
    if (!payload) return;
    // display-account-auto (review #2): creating a dedicated account grants it
    // this station, which makes the station access-managed — any current viewer
    // relying on open access loses it. Make that an explicit acknowledgment, not
    // a silent side effect.
    if (mode === 'create' && !window.confirm(
      `Create display account “${newName.trim()}” and grant it this station?\n\n`
      + 'This makes the station ACCESS-MANAGED: afterwards only this account (and '
      + 'anyone separately granted) can view it — current viewers relying on open '
      + 'access will lose it.')) {
      return;
    }
    setBusy(true); setError(null); setMsg(null);
    try {
      const r = await fetch(`/api/stations/${encodeURIComponent(stationId)}/display-credential`,
        { method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { setError(`provision: HTTP ${r.status} — ${j.error || ''}`); return; }
      setCred({ account_id: j.account_id, key_prefix: j.key_prefix, provisioned_at: j.provisioned_at });
      const createdNote = j.created_account
        ? ` Created a dedicated display account “${j.created_account.display_name}” (#${j.created_account.account_id}); this station is now access-managed — only this account (and any separately-granted users) can view it.`
        : '';
      setMsg('Provisioned. The screen picks up the new key on its next refresh — no re-flash.' + createdNote);
      setSelected(null); setQuery(''); setResults([]); setNewName('');
      if (onChanged) onChanged();
    } catch (e) { setError(String(e.message || e)); }
    finally { setBusy(false); }
  };

  const revoke = async () => {
    if (!window.confirm('Revoke the display key? The screen loses access until re-provisioned.')) return;
    setBusy(true); setError(null); setMsg(null);
    try {
      const r = await fetch(`/api/stations/${encodeURIComponent(stationId)}/display-credential`,
        { method: 'DELETE', credentials: 'same-origin' });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { setError(`revoke: HTTP ${r.status} — ${j.error || ''}`); return; }
      setCred(null);
      setMsg('Revoked. The key is dead and cleared from config; the board falls back to its baked key.');
      if (onChanged) onChanged();
    } catch (e) { setError(String(e.message || e)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <p style={{ fontSize: '0.82rem', color: '#555', marginTop: 0 }}>
        Provision the read key a <strong>display</strong> uses to fetch this station's rendered
        frames. aveq-admin mints it and delivers it through the station config — the screen picks it
        up with <strong>no re-flash</strong>. The key itself is a device secret and is never shown.
      </p>
      {error && <div style={{ color: '#c00', fontSize: '0.82rem', marginBottom: 8 }}>{error}</div>}
      {msg && <div style={{ color: '#363', fontSize: '0.82rem', marginBottom: 8 }}>{msg}</div>}

      <div style={{ margin: '10px 0', padding: '8px 10px', background: '#f6f7f9',
                    border: '1px solid #e2e5ea', borderRadius: 5, fontSize: '0.82rem' }}>
        {cred ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600, color: '#363' }}>Provisioned</span>
            <span style={{ color: '#555' }}>account <code>{cred.account_id}</code></span>
            {cred.key_prefix && <span style={{ color: '#555' }}>key <code>{cred.key_prefix}…</code></span>}
            {cred.provisioned_at && <span style={{ color: '#888' }}>{cred.provisioned_at}</span>}
            <button type="button" onClick={revoke} disabled={busy}
                    style={{ marginLeft: 'auto', ...btnDanger, opacity: busy ? 0.6 : 1 }}>Revoke</button>
          </div>
        ) : (
          <span style={{ color: '#888' }}>No display credential provisioned.</span>
        )}
      </div>

      {/* display-account-auto: pick an existing account OR create a dedicated one. */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 6, fontSize: '0.82rem' }}>
        <label style={{ display: 'flex', gap: 4, alignItems: 'center', cursor: 'pointer' }}>
          <input type="radio" name="displayCredMode" checked={mode === 'existing'}
                 onChange={() => setMode('existing')} />
          <span>Grant to existing account</span>
        </label>
        <label style={{ display: 'flex', gap: 4, alignItems: 'center', cursor: 'pointer' }}>
          <input type="radio" name="displayCredMode" checked={mode === 'create'}
                 onChange={() => setMode('create')} />
          <span>Create dedicated display account</span>
        </label>
      </div>

      {mode === 'existing' ? (
        <>
          <label style={{ display: 'block', fontSize: '0.82rem', color: '#444', marginBottom: 4 }}>
            {cred ? 'Re-point / rotate — pick an account:' : 'Grant to account:'}
          </label>
          <input type="text" value={query} onChange={(e) => setQuery(e.target.value)}
                 placeholder="Search by name or email (min 2 chars)…"
                 style={{ width: '100%', padding: '6px 8px', border: '1px solid #ccc',
                          borderRadius: 4, fontSize: '0.85rem', boxSizing: 'border-box' }} />
          {results.length > 0 && (
            <div style={{ border: '1px solid #e2e5ea', borderRadius: 4, marginTop: 4,
                          maxHeight: 160, overflowY: 'auto' }}>
              {results.map((a) => (
                <div key={a.id} onClick={() => setSelected(a)}
                     style={{ padding: '5px 8px', cursor: 'pointer', fontSize: '0.82rem',
                              background: selected && selected.id === a.id ? '#e8f0fe' : 'transparent',
                              borderBottom: '1px solid #f0f1f3' }}>
                  <strong>{a.display_name || `account ${a.id}`}</strong>
                  {a.email && <span style={{ color: '#888' }}> · {a.email}</span>}
                  <span style={{ color: '#bbb' }}> · #{a.id}</span>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          <label style={{ display: 'block', fontSize: '0.82rem', color: '#444', marginBottom: 4 }}>
            New display account name:
          </label>
          <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)}
                 placeholder="e.g. Lobby Screen"
                 style={{ width: '100%', padding: '6px 8px', border: '1px solid #ccc',
                          borderRadius: 4, fontSize: '0.85rem', boxSizing: 'border-box' }} />
          <p style={{ fontSize: '0.75rem', color: '#a60', marginTop: 6 }}>
            Creates a <strong>no-login</strong> account granted only this station and provisions its
            key in one step. Grants make the station <strong>access-managed</strong>: after this, only
            this display account (and anyone separately granted) can view it.
          </p>
        </>
      )}

      <div style={{ marginTop: 10 }}>
        {(() => {
          const ready = mode === 'create' ? newName.trim().length > 0 : !!selected;
          const label = busy ? 'Working…'
            : mode === 'create' ? 'Create account + provision key'
            : (cred ? 'Rotate / re-point key' : 'Provision key');
          return (
            <button type="button" onClick={provision} disabled={!ready || busy}
                    style={{ ...btnPrimary, opacity: (!ready || busy) ? 0.5 : 1,
                             cursor: (!ready || busy) ? 'default' : 'pointer' }}>
              {label}
              {mode === 'existing' && selected ? ` → ${selected.display_name || selected.id}` : ''}
            </button>
          );
        })()}
      </div>
      <p style={{ fontSize: '0.75rem', color: '#999', marginTop: 8 }}>
        Tip: use a dedicated low-access “display” account for shared screens (ADR D4). Default scope
        is read-only across stations so the screen follows <code>show_station</code> changes.
      </p>
    </div>
  );
}

const btnPrimary = { padding: '6px 12px', border: '1px solid #3b6fb0', borderRadius: 4,
                     background: '#4a80c4', color: '#fff', fontWeight: 600, fontSize: '0.82rem' };
const btnDanger = { padding: '4px 10px', border: '1px solid #c67', borderRadius: 4,
                    background: '#fff', color: '#a33', fontSize: '0.78rem' };
